import {
    LoanApplicant,
    Address,
    ResidenceOwnership,
    PropertyType,
    ResidenceType,
    EmploymentStatus,
    Citizenship,
    SpokenLanguage,
    LoanApplicationSubmitSource,
    LoanApplicantIdDocument,
    ApplicantEmployment,
    LoanApplication,
} from '@servicetitan/financing-api-client/src/prequals-api';
import { ApplicationData } from '../application/ApplicationDataStore';
import { formatAmount } from '@servicetitan/financing-utils-shared';
import { calculateIncomes } from '../../utils/calculateIncomes';
import { preparePrimaryApplicantSpouseData, prepareCoApplicantSpouseData } from '../spouseInfo';

export interface PreparePrequalificationDataParams {
    applicationData: ApplicationData;
    prequalId: string;
    tenantId: string;
    buId: string;
    jobId: string | null;
    customerId: string;
    salesAmount: number;
    downPayment: number;
    appliedOn: string;
    propertyType: PropertyType;
    planNumber: string;
    requestedAmount: number;
    technicianId?: string;
    technicianLastName?: string;
    technicianPhone?: string;
    technicianEmail?: string;
    source: LoanApplicationSubmitSource;
    hasMailingAddress?: boolean;
    referrer?: string;
}

// Format phone number to remove special characters
const formatPhoneNumber = (phone: string | undefined): string | undefined => {
    if (!phone) {
        return undefined;
    }
    return phone.replace(/[\(\)\s-]/g, '');
};

// Format SSN to remove dashes
const sanitizeSSN = (ssn: string | undefined): string | undefined => {
    if (!ssn) {
        return undefined;
    }
    return ssn.replace(/-/g, '');
};

export const preparePrequalificationData = ({
    applicationData,
    prequalId,
    tenantId,
    buId,
    jobId,
    customerId,
    salesAmount,
    downPayment,
    appliedOn,
    propertyType,
    planNumber,
    requestedAmount,
    technicianId,
    technicianLastName,
    technicianPhone,
    technicianEmail,
    source,
    hasMailingAddress,
    referrer,
}: PreparePrequalificationDataParams): LoanApplication => {
    // Use mailing address if available, otherwise use jobDetails.address
    const homeAddress = hasMailingAddress
        ? applicationData.mailingAddress.primaryApplicant?.address
        : applicationData.applicant.primaryApplicant?.jobDetails?.address;

    const propertyAddress = hasMailingAddress
        ? applicationData.mailingAddress.workPerformedAtAddress
            ? applicationData.mailingAddress.primaryApplicant?.address
            : applicationData.serviceProperty.address
        : applicationData.applicant.primaryApplicant?.jobDetails?.address;

    const hasCoApplicant = applicationData.coApplicant.hasCoApplicant;

    const primaryApplicantData = applicationData.applicant.primaryApplicant;
    const coApplicantData = hasCoApplicant ? applicationData.coApplicant.coApplicant : undefined;

    // FIN-15610 - sometimes number fields are formatted as strings, seems limited to Safari and Mobile Safari
    let monthlyHousingPayment = 0;
    let mortgagePayment = 0;
    // TODO: Figure out how to get the mortgage balance
    const mortgageBalance = 0;
    if (
        applicationData.serviceProperty.primaryApplicant &&
        'monthlyHousingPayment' in applicationData.serviceProperty.primaryApplicant
    ) {
        monthlyHousingPayment =
            applicationData.serviceProperty.primaryApplicant.monthlyHousingPayment ?? 0;
        monthlyHousingPayment =
            typeof monthlyHousingPayment === 'string'
                ? Number(formatAmount(monthlyHousingPayment))
                : monthlyHousingPayment;
        mortgagePayment = monthlyHousingPayment;
    }

    const { grossMonthlyIncome = 0, annualIncome = 0 } = calculateIncomes({
        grossMonthlyIncome: primaryApplicantData?.employment?.grossMonthlyIncome,
        annualIncome: primaryApplicantData?.annualIncome,
    });

    // Build primary applicant
    const primaryApplicant: LoanApplicant = {
        firstName: primaryApplicantData?.firstName,
        middleInitial: primaryApplicantData?.middleInitial,
        lastName: primaryApplicantData?.lastName,
        dateOfBirth: primaryApplicantData?.dateOfBirth ?? '',
        ssn: sanitizeSSN(primaryApplicantData?.ssn),
        emailAddress: primaryApplicantData?.email,
        primaryPhone: formatPhoneNumber(primaryApplicantData?.primaryPhone),
        alternatePhone: formatPhoneNumber(primaryApplicantData?.alternatePhone),
        workPhone: formatPhoneNumber(primaryApplicantData?.workPhone),
        housingType:
            (primaryApplicantData?.jobDetails?.residenceType as unknown as ResidenceOwnership) ??
            (applicationData.serviceProperty.primaryApplicant
                ?.housingType as unknown as ResidenceOwnership),
        monthlyHousingPayment,
        propertyType:
            (applicationData.serviceProperty.primaryApplicant?.propertyType as PropertyType) ??
            PropertyType.NotSet,
        address: homeAddress as Address,
        applicantId: primaryApplicantData?.applicantId as LoanApplicantIdDocument,
        employment: {
            grossMonthlyIncome,
            employerName: primaryApplicantData?.employment?.employerName,
            employerPhone: formatPhoneNumber(primaryApplicantData?.employment?.employerPhone),
        } as ApplicantEmployment,
        annualIncome,
        residenceType: (primaryApplicantData?.typeOfHome as ResidenceType) ?? ResidenceType.NotSet,
        employmentStatus:
            (primaryApplicantData?.employment?.status as EmploymentStatus) ??
            EmploymentStatus.NotSet,
        citizenship: (primaryApplicantData?.citizenship as Citizenship) ?? Citizenship.NotSet,
        spokenLanguage:
            (primaryApplicantData?.spokenLanguage as SpokenLanguage) ?? SpokenLanguage.NotSet,
        signature: primaryApplicantData?.signature,
        ...preparePrimaryApplicantSpouseData(applicationData, homeAddress),
    };

    // Build co-applicant if present
    let coApplicant: LoanApplicant | undefined = undefined;
    if (hasCoApplicant && coApplicantData) {
        const {
            grossMonthlyIncome: coApplicantGrossMonthlyIncome = 0,
            annualIncome: coApplicantAnnualIncome = 0,
        } = calculateIncomes({
            grossMonthlyIncome: coApplicantData.employment?.grossMonthlyIncome,
            annualIncome: coApplicantData.annualIncome,
        });

        const coApplicantAddress =
            coApplicantData.isAddressSameAsPrimary === true ? homeAddress : coApplicantData.address;

        coApplicant = {
            firstName: coApplicantData.firstName,
            middleInitial: coApplicantData.middleInitial,
            lastName: coApplicantData.lastName,
            dateOfBirth: coApplicantData.dateOfBirth ?? '',
            ssn: sanitizeSSN(coApplicantData.ssn),
            emailAddress: coApplicantData.email,
            primaryPhone: formatPhoneNumber(coApplicantData.primaryPhone),
            alternatePhone: formatPhoneNumber(coApplicantData.alternatePhone),
            workPhone: formatPhoneNumber(coApplicantData.workPhone),
            housingType:
                coApplicantData.jobDetails?.residenceType ??
                (ResidenceOwnership.NotSet as ResidenceOwnership),
            monthlyHousingPayment: 0,
            propertyType: PropertyType.NotSet,
            address: coApplicantAddress as Address,
            applicantId: coApplicantData.applicantId as LoanApplicantIdDocument,
            employment: {
                grossMonthlyIncome: coApplicantGrossMonthlyIncome,
                employerName: coApplicantData.employment?.employerName,
                employerPhone: formatPhoneNumber(coApplicantData.employment?.employerPhone),
            } as ApplicantEmployment,
            annualIncome: coApplicantAnnualIncome,
            residenceType: (coApplicantData.typeOfHome as ResidenceType) ?? ResidenceType.NotSet,
            employmentStatus:
                (coApplicantData.employment?.status as EmploymentStatus) ?? EmploymentStatus.NotSet,
            citizenship: (coApplicantData.citizenship as Citizenship) ?? Citizenship.NotSet,
            spokenLanguage:
                (coApplicantData.spokenLanguage as SpokenLanguage) ?? SpokenLanguage.NotSet,
            signature: coApplicantData.signature,
            ...prepareCoApplicantSpouseData(
                applicationData,
                coApplicantData.jobDetails,
                coApplicantAddress,
            ),
        };
    }

    return {
        prequalId,
        tenantId,
        jobId,
        customerId,
        buId,
        salesAmount,
        downPayment,
        appliedOn,
        propertyType,
        planNumber,
        requestedAmount,
        technicianId,
        technicianLastName,
        technicianPhone: formatPhoneNumber(technicianPhone),
        technicianEmail,
        mortgageBalance: mortgageBalance ?? 0,
        mortgagePayment: mortgagePayment ?? 0,
        primaryApplicant,
        coApplicant,
        address: propertyAddress as Address,
        source,
        referrer,
    };
};
